import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

export interface OpenSkyFlightInfo {
  icao24: string;
  callsign: string;
  originCountry: string;
  latitude: number;
  longitude: number;
  altitudeMeters: number;
  altitudeFeet: number;
  velocityKnots: number;
  velocityKmh: number;
  headingDeg: number;
  verticalRateMs: number;
  onGround: boolean;
  squawk?: string;
  lastSeenTimestamp: number;
  isNigeria: boolean;
  route?: string;
  aircraftModel?: string;
  registration?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OpenSkyCentralService {
  private activeAircraftPool = new Map<string, OpenSkyFlightInfo>();
  private candidatePool = new Map<string, OpenSkyFlightInfo>();
  private allCachedAircraftMap = new Map<string, OpenSkyFlightInfo>();

  private activeAircraftSubject = new BehaviorSubject<OpenSkyFlightInfo[]>([]);
  public activeAircraft$: Observable<OpenSkyFlightInfo[]> = this.activeAircraftSubject.asObservable();

  private totalApiCountSubject = new BehaviorSubject<number>(0);
  public totalApiCount$: Observable<number> = this.totalApiCountSubject.asObservable();

  private isFetchingSubject = new BehaviorSubject<boolean>(false);
  public isFetching$: Observable<boolean> = this.isFetchingSubject.asObservable();

  private isRateLimitedSubject = new BehaviorSubject<boolean>(false);
  public isRateLimited$: Observable<boolean> = this.isRateLimitedSubject.asObservable();

  private pollTimer: any = null;
  private isInitialized = false;
  private lastFetchTime = 0;
  private rateLimitedUntil = 0;
  private backoffDelayMs = 15000; // 15 sec live update interval
  private readonly TARGET_POOL_MIN = 80;
  private readonly TARGET_POOL_MAX = 200;
  private readonly STALE_THRESHOLD_MS = 180000; // 3 minutes

  constructor(private http: HttpClient) {}

  public startCentralService(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.fetchLiveFlightRadarData();

    // Centralized timer loop
    this.scheduleNextFetch(this.backoffDelayMs);
  }

  public stopCentralService(): void {
    this.isInitialized = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  public get isFetching(): boolean {
    return this.isFetchingSubject.getValue();
  }

  private scheduleNextFetch(delayMs: number): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
    this.pollTimer = setTimeout(() => {
      this.fetchLiveFlightRadarData();
    }, delayMs);
  }

  public getActiveAircraft(): OpenSkyFlightInfo[] {
    return Array.from(this.activeAircraftPool.values());
  }

  public getTotalApiCount(): number {
    return this.totalApiCountSubject.getValue();
  }

  public async fetchOpenSkyData(): Promise<void> {
    return this.fetchLiveFlightRadarData();
  }

  /**
   * Main fetch method calling real-time live flight radar data.
   * Uses live satellite + ADS-B radar feeds (Flightradar24 / OpenSky) to guarantee real live Nigerian & Global flights.
   */
  public async fetchLiveFlightRadarData(): Promise<void> {
    const now = Date.now();
    if (now < this.rateLimitedUntil) {
      this.extrapolateActivePositions(now);
      this.scheduleNextFetch(Math.max(5000, this.rateLimitedUntil - now));
      return;
    }

    this.isFetchingSubject.next(true);

    try {
      const freshFlights: OpenSkyFlightInfo[] = [];

      // 1. Fetch Nigeria Regional Real-time Satellite / ADS-B Live Radar Feed
      const nigeriaFeedUrl = '/api/fr24/zones/fcgi/feed.js?bounds=15.5,3.5,2.0,15.5&faa=1&satellite=1&mlat=1&flarm=1&adsb=1&gnd=1&air=1&vehicles=0&estimated=1&maxage=14400&gliders=0&stats=1';
      const nigeriaDirectUrl = 'https://data-cloud.flightradar24.com/zones/fcgi/feed.js?bounds=15.5,3.5,2.0,15.5&faa=1&satellite=1&mlat=1&flarm=1&adsb=1&gnd=1&air=1&vehicles=0&estimated=1&maxage=14400&gliders=0&stats=1';

      const nigeriaData = await this.fetchJsonWithFallback(nigeriaFeedUrl, nigeriaDirectUrl);
      if (nigeriaData) {
        const parsedNigeria = this.parseFlightradar24Feed(nigeriaData, now, true);
        freshFlights.push(...parsedNigeria);
      }

      // 2. Fetch Global Real-time Live Radar Feed
      const globalFeedUrl = '/api/fr24/zones/fcgi/feed.js?faa=1&satellite=1&mlat=1&flarm=1&adsb=1&gnd=1&air=1&vehicles=0&estimated=1&maxage=14400&gliders=0&stats=1';
      const globalDirectUrl = 'https://data-cloud.flightradar24.com/zones/fcgi/feed.js?faa=1&satellite=1&mlat=1&flarm=1&adsb=1&gnd=1&air=1&vehicles=0&estimated=1&maxage=14400&gliders=0&stats=1';

      const globalData = await this.fetchJsonWithFallback(globalFeedUrl, globalDirectUrl);
      if (globalData) {
        const parsedGlobal = this.parseFlightradar24Feed(globalData, now, false);
        freshFlights.push(...parsedGlobal);
      }

      // 3. Optional OpenSky Network Feed Fallback if total flights are low
      if (freshFlights.length < 30) {
        const openSkyData = await this.fetchOpenSkyStates();
        if (openSkyData && Array.isArray(openSkyData)) {
          const parsedOpenSky = this.parseOpenSkyStates(openSkyData, now);
          freshFlights.push(...parsedOpenSky);
        }
      }

      if (freshFlights.length > 0) {
        this.processCombinedFreshFlights(freshFlights, now);
        this.isRateLimitedSubject.next(false);
        this.backoffDelayMs = 15000;
      } else {
        this.extrapolateActivePositions(now);
      }
    } catch (err) {
      console.warn('Live Flight Radar: Fetch error:', err);
      this.extrapolateActivePositions(now);
      this.backoffDelayMs = 20000;
    } finally {
      this.isFetchingSubject.next(false);
      this.scheduleNextFetch(this.backoffDelayMs);
    }
  }

  /**
   * Helper to fetch JSON from primary proxy route with graceful direct fallback
   */
  private async fetchJsonWithFallback(proxyUrl: string, directUrl: string): Promise<any | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      let response = await window.fetch(proxyUrl, {
        signal: controller.signal
      });

      // If proxy returns 404/500 or HTML (e.g. dev server restarted without proxy reloaded), try direct
      if (!response.ok || (response.headers.get('content-type') || '').includes('text/html')) {
        const directController = new AbortController();
        const directTimeoutId = setTimeout(() => directController.abort(), 8000);
        response = await window.fetch(directUrl, {
          signal: directController.signal
        });
        clearTimeout(directTimeoutId);
      }

      clearTimeout(timeoutId);

      if (response && response.ok) {
        return await response.json();
      }
    } catch (e) {
      // Ignore fallback errors and return null
    }
    return null;
  }

  /**
   * Safe fetch for OpenSky Network states
   */
  private async fetchOpenSkyStates(): Promise<any[] | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await window.fetch('/api/opensky/states/all', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (response && response.ok) {
        const data = await response.json();
        if (data && Array.isArray(data.states)) {
          return data.states;
        }
      }
    } catch (e) {
      // Ignore
    }
    return null;
  }

  /**
   * Parses live Flightradar24 satellite & ADS-B aircraft feed
   */
  private parseFlightradar24Feed(data: any, now: number, forceNigeria = false): OpenSkyFlightInfo[] {
    const list: OpenSkyFlightInfo[] = [];
    if (!data || typeof data !== 'object') return list;

    Object.entries(data).forEach(([key, v]) => {
      if (!Array.isArray(v) || v.length < 13) return;

      const icaoRaw = (key || '').trim().toUpperCase();
      const lat = typeof v[1] === 'number' ? v[1] : NaN;
      const lon = typeof v[2] === 'number' ? v[2] : NaN;
      const heading = typeof v[3] === 'number' ? v[3] : 0;
      const altFeet = typeof v[4] === 'number' ? v[4] : 0;
      const speedKnots = typeof v[5] === 'number' ? v[5] : 0;
      const squawk = v[6] ? String(v[6]) : undefined;
      const aircraftModel = v[8] ? String(v[8]) : undefined;
      const registration = v[9] ? String(v[9]) : undefined;
      const fromAirport = v[11] ? String(v[11]) : '';
      const toAirport = v[12] ? String(v[12]) : '';
      const flightNumber = v[13] ? String(v[13]) : '';
      const onGround = v[14] === 1;
      const verticalRateFpm = typeof v[15] === 'number' ? v[15] : 0;
      const callsignRaw = v[16] ? String(v[16]) : (flightNumber || registration || `FLT-${icaoRaw}`);
      const airline = v[17] ? String(v[17]) : '';

      if (
        !icaoRaw ||
        isNaN(lat) ||
        isNaN(lon) ||
        lat < -90 ||
        lat > 90 ||
        lon < -180 ||
        lon > 180 ||
        onGround
      ) {
        return;
      }

      const altMeters = Math.round(altFeet * 0.3048);
      const speedKmh = Math.round(speedKnots * 1.852);
      const verticalRateMs = Math.round((verticalRateFpm * 0.00508) * 10) / 10;

      // Check if aircraft is currently in Nigerian airspace
      const isInsideNigeriaBounds = lat >= 4.0 && lat <= 14.5 && lon >= 2.5 && lon <= 14.5;
      const isNigerianAirport = ['LOS', 'ABV', 'PHC', 'KAN', 'ENU', 'BNI', 'CBQ', 'QOW', 'QUO', 'KAD', 'MIU', 'SKO', 'YOL'].includes(fromAirport) ||
                                ['LOS', 'ABV', 'PHC', 'KAN', 'ENU', 'BNI', 'CBQ', 'QOW', 'QUO', 'KAD', 'MIU', 'SKO', 'YOL'].includes(toAirport);
      const isNigeria = forceNigeria || isInsideNigeriaBounds || isNigerianAirport;

      let countryLabel = airline || 'International';
      let routeStr = '';
      if (fromAirport && toAirport) {
        routeStr = `${fromAirport} → ${toAirport}`;
        countryLabel = `${airline || 'Flight'} (${routeStr})`;
      } else if (fromAirport) {
        routeStr = `From ${fromAirport}`;
        countryLabel = `${airline || 'Flight'} (${routeStr})`;
      } else if (isInsideNigeriaBounds) {
        countryLabel = airline ? `${airline} (Nigeria)` : 'Nigeria Airspace';
      }

      const info: OpenSkyFlightInfo = {
        icao24: icaoRaw,
        callsign: callsignRaw,
        originCountry: countryLabel,
        latitude: lat,
        longitude: lon,
        altitudeMeters: altMeters,
        altitudeFeet: altFeet,
        velocityKnots: speedKnots,
        velocityKmh: speedKmh,
        headingDeg: heading,
        verticalRateMs,
        onGround: false,
        squawk,
        lastSeenTimestamp: now,
        isNigeria,
        route: routeStr || undefined,
        aircraftModel,
        registration
      };

      list.push(info);
    });

    return list;
  }

  /**
   * Parses live OpenSky Network state vectors
   */
  private parseOpenSkyStates(rawStates: any[], now: number): OpenSkyFlightInfo[] {
    const list: OpenSkyFlightInfo[] = [];
    rawStates.forEach((v) => {
      if (!Array.isArray(v)) return;

      const icaoRaw = (v[0] || '').trim().toUpperCase();
      const callsignRaw = (v[1] || '').trim();
      const originCountry = (v[2] || 'International').trim();
      const lon = v[5];
      const lat = v[6];
      const baroAlt = v[7];
      const geoAlt = v[13];
      const onGround = !!v[8];
      const velocityMs = v[9];
      const heading = v[10];
      const verticalRateMs = v[11];
      const squawk = v[14];

      if (
        !icaoRaw ||
        typeof lat !== 'number' ||
        typeof lon !== 'number' ||
        isNaN(lat) ||
        isNaN(lon) ||
        lat < -90 ||
        lat > 90 ||
        lon < -180 ||
        lon > 180 ||
        onGround
      ) {
        return;
      }

      const altMeters = typeof geoAlt === 'number' ? geoAlt : (typeof baroAlt === 'number' ? baroAlt : 3000);
      const velMs = typeof velocityMs === 'number' ? velocityMs : 200;
      const headDeg = typeof heading === 'number' ? heading : 0;
      const vertRate = typeof verticalRateMs === 'number' ? verticalRateMs : 0;

      const isInsideNigeriaBounds = lat >= 4.0 && lat <= 14.5 && lon >= 2.5 && lon <= 14.5;
      const isNigeria = isInsideNigeriaBounds || originCountry.toLowerCase() === 'nigeria';

      let countryName = originCountry;
      if (isInsideNigeriaBounds && (countryName === 'International' || !countryName)) {
        countryName = 'Nigeria Airspace';
      }

      list.push({
        icao24: icaoRaw,
        callsign: callsignRaw || `FLT-${icaoRaw}`,
        originCountry: countryName,
        latitude: lat,
        longitude: lon,
        altitudeMeters: Math.round(altMeters),
        altitudeFeet: Math.round(altMeters * 3.28084),
        velocityKnots: Math.round(velMs * 1.94384),
        velocityKmh: Math.round(velMs * 3.6),
        headingDeg: Math.round(headDeg),
        verticalRateMs: Math.round(vertRate * 10) / 10,
        onGround: false,
        squawk: squawk ? (squawk + '').trim() : undefined,
        lastSeenTimestamp: now,
        isNigeria
      });
    });
    return list;
  }

  /**
   * Process and update active pool prioritizing real Nigeria airspace flights.
   */
  private processCombinedFreshFlights(freshFlights: OpenSkyFlightInfo[], now: number): void {
    const freshAircraftMap = new Map<string, OpenSkyFlightInfo>();
    const nigeriaList: OpenSkyFlightInfo[] = [];
    const globalList: OpenSkyFlightInfo[] = [];

    freshFlights.forEach((flight) => {
      if (freshAircraftMap.has(flight.icao24)) return;

      const validatedFlight = this.validateMovement(flight, now);
      freshAircraftMap.set(flight.icao24, validatedFlight);
      this.allCachedAircraftMap.set(flight.icao24, validatedFlight);

      if (validatedFlight.isNigeria) {
        nigeriaList.push(validatedFlight);
      } else {
        globalList.push(validatedFlight);
      }
    });

    this.totalApiCountSubject.next(freshAircraftMap.size);
    this.lastFetchTime = now;

    // 1. Update existing active aircraft
    this.activeAircraftPool.forEach((aircraft, icao24) => {
      const fresh = freshAircraftMap.get(icao24);
      if (fresh) {
        this.activeAircraftPool.set(icao24, fresh);
      } else if (now - aircraft.lastSeenTimestamp > this.STALE_THRESHOLD_MS) {
        this.activeAircraftPool.delete(icao24);
      } else {
        this.extrapolateSingleAircraft(aircraft, (now - this.lastFetchTime) / 1000);
      }
    });

    // 2. HIGHEST PRIORITY: Add ALL real live Nigeria flights into active pool
    nigeriaList.forEach((nFlight) => {
      this.activeAircraftPool.set(nFlight.icao24, nFlight);
    });

    // 3. Fill candidate global pool
    this.candidatePool.clear();
    globalList.forEach((gFlight) => {
      if (this.activeAircraftPool.has(gFlight.icao24)) {
        return;
      }
      if (this.activeAircraftPool.size < this.TARGET_POOL_MAX) {
        this.activeAircraftPool.set(gFlight.icao24, gFlight);
      } else {
        this.candidatePool.set(gFlight.icao24, gFlight);
      }
    });

    // 4. Promote from candidate pool if below target min
    if (this.activeAircraftPool.size < this.TARGET_POOL_MIN && this.candidatePool.size > 0) {
      const candidates = Array.from(this.candidatePool.values());
      for (const cand of candidates) {
        if (this.activeAircraftPool.size >= this.TARGET_POOL_MAX) break;
        this.activeAircraftPool.set(cand.icao24, cand);
        this.candidatePool.delete(cand.icao24);
      }
    }

    this.notifyActivePoolUpdated();
  }

  /**
   * Validates movement to prevent unrealistic jumps.
   */
  private validateMovement(newFlight: OpenSkyFlightInfo, now: number): OpenSkyFlightInfo {
    const existing = this.activeAircraftPool.get(newFlight.icao24) || this.allCachedAircraftMap.get(newFlight.icao24);
    if (!existing) {
      return newFlight;
    }

    const timeDeltaSec = (now - existing.lastSeenTimestamp) / 1000;
    if (timeDeltaSec <= 0) {
      return newFlight;
    }

    const distKm = this.calculateDistanceKm(
      existing.latitude,
      existing.longitude,
      newFlight.latitude,
      newFlight.longitude
    );

    const impliedSpeedKmh = (distKm / timeDeltaSec) * 3600;

    if (impliedSpeedKmh > 1800 && distKm > 100) {
      return {
        ...newFlight,
        latitude: existing.latitude,
        longitude: existing.longitude,
        headingDeg: existing.headingDeg
      };
    }

    return newFlight;
  }

  private calculateDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private extrapolateActivePositions(now: number): void {
    const dtSec = 5.0;
    this.activeAircraftPool.forEach((f) => {
      this.extrapolateSingleAircraft(f, dtSec);
    });
    this.notifyActivePoolUpdated();
  }

  private extrapolateSingleAircraft(f: OpenSkyFlightInfo, dtSec: number): void {
    const speedMps = Math.max((f.velocityKnots || 200) * 0.514444, 50);
    const headingRad = (f.headingDeg * Math.PI) / 180.0;

    const deltaLat = (speedMps * dtSec * Math.cos(headingRad)) / 111139.0;
    const cosLat = Math.max(Math.cos((f.latitude * Math.PI) / 180.0), 0.1);
    const deltaLon = (speedMps * dtSec * Math.sin(headingRad)) / (111139.0 * cosLat);

    f.latitude += deltaLat;
    f.longitude += deltaLon;

    if (f.longitude > 180) f.longitude -= 360;
    if (f.longitude < -180) f.longitude += 360;
    if (f.latitude > 85) f.latitude = 85;
    if (f.latitude < -85) f.latitude = -85;
  }

  private notifyActivePoolUpdated(): void {
    this.activeAircraftSubject.next(Array.from(this.activeAircraftPool.values()));
  }
}

